import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SparkleRegular, BookOpenRegular, SendRegular, DeleteRegular, DismissRegular, CheckmarkCircleRegular, ErrorCircleRegular, WarningRegular } from '@fluentui/react-icons';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTextSize } from '../contexts/TextSizeContext';
import { ChatMessage } from './ChatMessage';
import { useSocketConnectionWithACK, streamingApiClient, socketManager } from '../../donexus-frontend/endpoint/streaming';
import incidentCommunicationsService from '../services/incidentCommunicationsService';
import emailConnectionsService from '../services/emailConnectionsService';
import emailMessagesService from '../services/emailMessagesService';

// Generate a stable unique ID for chat messages
function generateMessageId() {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Toast Notification Component for Office Add-in
function Toast({ message, type = 'info', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000); // Auto-dismiss after 5 seconds
    return () => clearTimeout(timer);
  }, [onClose]);

  const typeStyles = {
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      icon: <CheckmarkCircleRegular className="h-5 w-5 text-green-600" />
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      icon: <ErrorCircleRegular className="h-5 w-5 text-red-600" />
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-800',
      icon: <WarningRegular className="h-5 w-5 text-yellow-600" />
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      icon: <SparkleRegular className="h-5 w-5 text-blue-600" />
    }
  };

  const style = typeStyles[type] || typeStyles.info;

  return (
    <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4 animate-slide-down`}>
      <div className={`${style.bg} ${style.border} border rounded-lg shadow-lg p-4`}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">{style.icon}</div>
          <div className={`flex-1 ${style.text} text-sm leading-relaxed`}>{message}</div>
          <button
            onClick={onClose}
            className={`flex-shrink-0 ${style.text} hover:opacity-70 transition-opacity`}
            aria-label="Close notification"
          >
            <DismissRegular className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ActionPromptBubbles component
function ActionPromptBubbles({ actionPrompts, onPromptClick, className }) {
  const [clickedPromptId, setClickedPromptId] = useState(null);
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();

  if (!actionPrompts || actionPrompts.length === 0) {
    return null;
  }

  // Sort action prompts by priority: action_mail_1, action_mail_2, action_tool
  const sortedPrompts = [...actionPrompts].sort((a, b) => {
    const getPriority = (id) => {
      if (id === 'action_mail_1') return 1;
      if (id === 'action_mail_2') return 2;
      if (id === 'action_tool') return 3;
      return 4; // fallback for any other types
    };
    return getPriority(a.id) - getPriority(b.id);
  });

  const handlePromptClick = (prompt) => {
    // Prevent multiple clicks on the same prompt
    if (clickedPromptId === prompt.id) {
      console.log('⏭️ Ignoring duplicate click on action prompt:', prompt.id);
      return;
    }
    
    setClickedPromptId(prompt.id);
    onPromptClick(prompt);
    
    // Reset after a delay to allow re-clicking if needed
    setTimeout(() => {
      setClickedPromptId(null);
    }, 3000);
  };

  return (
    <div className={`flex flex-col gap-1.5 mb-3 ${className || ''}`}>
      {/* AI Suggestion Header */}
      <div className={`flex items-center gap-1.5 ${currentTextSize.buttonText} font-medium text-gray-500 mb-0.5 px-1`}>
        <span>{t('aiAssistant.kiraSuggestions')}</span>
      </div>

      {/* Action Prompt Bubbles */}
      <div className="flex flex-col gap-1.5">
        {sortedPrompts.map((prompt) => (
          <button
            key={prompt.id}
            onClick={() => handlePromptClick(prompt)}
            disabled={clickedPromptId === prompt.id}
            className={`
              group relative h-auto py-2 px-3 rounded-xl
              bg-white/90 backdrop-blur-sm
              border border-gray-200/80
              hover:bg-white hover:border-blue-300
              hover:shadow-sm
              transition-all duration-150 ease-in-out
              text-left justify-start
              max-w-[280px]
              ${clickedPromptId === prompt.id ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            title={prompt.question_for_details || prompt.action_prompt}
          >
            {/* Speech bubble tail */}
            <div className="absolute -bottom-1 left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200/80 group-hover:border-t-blue-300 transition-colors" />
            
            <div className="flex items-center gap-2 w-full">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="h-5 w-5 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                  <SparkleRegular className="h-2.5 w-2.5 text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className={`${currentTextSize.bodyText} font-medium text-gray-700 group-hover:text-gray-900 leading-snug`}>
                  {prompt.display_action || prompt.action_prompt}
                </div>
              </div>

              {/* Arrow indicator */}
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <svg
                  className="h-3 w-3 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AIAssistant({ contextVersion, onSocketResetReady, onIndexStatusChange }) {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(null);
  const [actionPrompts, setActionPrompts] = useState([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [showActionPrompts, setShowActionPrompts] = useState(true);
  const [deepResearchEnabled, setDeepResearchEnabled] = useState(false);
  const [isEmailIndexed, setIsEmailIndexed] = useState(true);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  
  // Toast notification state
  const [toast, setToast] = useState(null);
  
  // Helper function to show toast notifications (replaces alert())
  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);
  
  // Room and session management
  const [roomId, setRoomId] = useState(null);
  const [responseId, setResponseId] = useState(null);
  const [toolResultsBuffer, setToolResultsBuffer] = useState(false);
  const [isRoomReady, setIsRoomReady] = useState(false);
  const [incidentId, setIncidentId] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  
  // Streaming state management
  const [chunkBuffer, setChunkBuffer] = useState(new Map());
  const [expectedFinalSequence, setExpectedFinalSequence] = useState(null);
  const [isStreamingComplete, setIsStreamingComplete] = useState(false);
  
  // Pending action prompt
  const [pendingActionPrompt, setPendingActionPrompt] = useState(null);
  const processingActionPromptRef = useRef(null);
  
  // Sources management
  const [streamingSources, setStreamingSources] = useState(new Map());
  const [currentStreamingSources, setCurrentStreamingSources] = useState([]);
  
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const { t } = useLanguage();
  const { user, getAccessToken } = useAuth();
  const { currentTextSize } = useTextSize();

  // Initialize socket connection when user is authenticated
  useEffect(() => {
    console.log('🔍 AIAssistant Socket Init Check:', {
      hasUser: !!user,
      userId: user?.id,
      isConnected: socketManager.isConnected(),
      hasToken: !!getAccessToken(),
      tokenPreview: getAccessToken() ? `${getAccessToken().substring(0, 20)}...` : 'null'
    });
    
    if (user && !socketManager.isConnected()) {
      const token = getAccessToken();
      if (token) {
        console.log('🔌 AIAssistant: Initializing socket connection');
        console.log('   User ID:', user.id);
        console.log('   Token length:', token.length);
        console.log('   Token preview:', token.substring(0, 50) + '...');
        socketManager.connect(user, token);
      } else {
        console.warn('⚠️ AIAssistant: User exists but no token available');
        console.warn('   User:', user);
      }
    }
  }, [user, getAccessToken]);

  // Socket connection with ACK support
  const {
    socket,
    isConnected,
    connectionMetrics,
    setupMessageListener,
    joinRoom: socketJoinRoom,
    leaveRoom: socketLeaveRoom,
    createAndJoinSessionRoom,
    currentRoom,
    forceReset
  } = useSocketConnectionWithACK();

  // Expose the forceReset function to parent component
  useEffect(() => {
    if (onSocketResetReady && forceReset) {
      onSocketResetReady(forceReset);
    }
  }, [onSocketResetReady, forceReset]);

  // Helper function to build response from buffer in sequence order
  const buildResponseFromBuffer = useCallback((buffer) => {
    if (buffer.size === 0) return '';
    
    const sequences = Array.from(buffer.keys()).sort((a, b) => a - b);
    const minSequence = sequences[0];
    
    let response = '';
    let currentSequence = minSequence;
    
    while (buffer.has(currentSequence)) {
      const chunk = buffer.get(currentSequence);
      if (chunk !== undefined) {
        response += chunk;
      }
      currentSequence++;
    }
    
    return response;
  }, []);

  // Check if all chunks are received
  const areAllChunksReceived = useCallback((buffer, finalSequence) => {
    if (finalSequence === null || buffer.size === 0) return false;
    
    const sequences = Array.from(buffer.keys()).sort((a, b) => a - b);
    const minSequence = sequences[0];
    
    for (let i = minSequence; i <= finalSequence; i++) {
      if (!buffer.has(i)) {
        console.log(`Missing chunk at sequence ${i}`);
        return false;
      }
    }
    
    console.log(`All chunks received from ${minSequence} to ${finalSequence}`);
    return true;
  }, []);

  // Finalize streaming completion
  const finalizeStreamingCompletion = useCallback((completionData) => {
    console.log('🏁 Finalizing streaming completion');
    
    setIsStreamingComplete(true);
    setIsStreaming(false);
    setCurrentProgress(null);
    
    // Update response ID from completion data
    if (completionData?.payload?.response_id) {
      setResponseId(completionData.payload.response_id);
    }
    
    // Update tool results buffer from completion data
    if (completionData?.payload?.tool_results_buffer !== undefined) {
      setToolResultsBuffer(completionData.payload.tool_results_buffer);
    }
    
    // Finalize the response and add to conversation
    setChunkBuffer(prevBuffer => {
      const finalResponse = buildResponseFromBuffer(prevBuffer);
      setStreamingResponse(finalResponse);
      
      if (finalResponse.trim()) {
        const aiMessage = {
          id: generateMessageId(),
          role: 'assistant',
          content: finalResponse,
          timestamp: new Date(),
          sources: currentStreamingSources
        };
        setConversation(prev => [...prev, aiMessage]);
        
        // Reset current streaming sources for next message
        setCurrentStreamingSources([]);
      }
      
      return prevBuffer;
    });
    
    console.log('✅ Streaming completion finalized');
  }, [buildResponseFromBuffer, currentStreamingSources]);

  // Handle streaming chunk
  const handleStreamingChunk = useCallback((data) => {
    const messageRoomId = data.roomId;
    
    if (!roomId || messageRoomId !== roomId) {
      console.log('🚫 Ignoring chunk - room mismatch');
      return;
    }
    
    const payload = data.payload || data.data || data;
    const sequence = payload?.sequence;
    const chunkText = payload?.text || payload || '';
    
    console.log('📦 Processing streaming chunk:', { sequence, chunkText: chunkText.substring(0, 50) });
    
    if (sequence !== undefined) {
      setIsStreaming(true);
      setCurrentProgress(null);
      
      setChunkBuffer(prevBuffer => {
        const newBuffer = new Map(prevBuffer);
        newBuffer.set(sequence, chunkText);
        
        const newResponse = buildResponseFromBuffer(newBuffer);
        setStreamingResponse(newResponse);
        
        return newBuffer;
      });
    }
  }, [roomId, buildResponseFromBuffer]);

  // Handle streaming complete
  const handleStreamingComplete = useCallback((data) => {
    const messageRoomId = data.roomId;
    
    if (!roomId || messageRoomId !== roomId) {
      console.log('🚫 Ignoring completion - room mismatch');
      return;
    }
    
    console.log('🎯 Processing streaming completion');
    
    const payload = data.payload || data.data || data;
    const sequence = payload?.sequence;
    const finalChunkText = payload?.text || '';
    
    if (sequence !== undefined) {
      setExpectedFinalSequence(sequence);
    }
    
    // Process final chunk if present
    if (sequence !== undefined && finalChunkText) {
      setChunkBuffer(prevBuffer => {
        const newBuffer = new Map(prevBuffer);
        newBuffer.set(sequence, finalChunkText);
        
        const finalResponse = buildResponseFromBuffer(newBuffer);
        setStreamingResponse(finalResponse);
        
        if (areAllChunksReceived(newBuffer, sequence)) {
          setTimeout(() => finalizeStreamingCompletion(data), 0);
        }
        
        return newBuffer;
      });
    } else {
      setTimeout(() => finalizeStreamingCompletion(data), 0);
    }
  }, [roomId, buildResponseFromBuffer, areAllChunksReceived, finalizeStreamingCompletion]);

  // Handle progress message
  const handleProgressMessage = useCallback((data) => {
    const messageRoomId = data.roomId;
    
    if (!roomId || messageRoomId !== roomId) {
      return;
    }
    
    const payload = data.payload || data.data || data;
    
    // If tool_name is present, translate it
    if (payload && payload.tool_name) {
      const translationKey = `incident.chat_agent.tools.${payload.tool_name}`;
      const progressText = t(translationKey, { defaultValue: payload.tool_name });
      setCurrentProgress(progressText);
    } else {
      // Otherwise use text or fallback to empty string
      const progressText = payload?.text || payload || '';
      setCurrentProgress(progressText);
    }
  }, [roomId, t]);

  // Handle streaming error
  const handleStreamingError = useCallback((data) => {
    const messageRoomId = data.roomId;
    
    if (!roomId || messageRoomId !== roomId) {
      return;
    }
    
    const payload = data.payload || data.data || data;
    const errorMessage = payload?.message || payload || 'An error occurred';
    
    console.error('❌ Streaming error:', errorMessage);
    
    setCurrentProgress(null);
    setIsStreaming(false);
    setStreamingResponse('');
    setChunkBuffer(new Map());
    setIsStreamingComplete(false);
    setCurrentStreamingSources([]);
    
    const errorMsg = {
      id: generateMessageId(),
      role: 'assistant',
      content: `❌ **Error**: ${errorMessage}`,
      timestamp: new Date()
    };
    setConversation(prev => [...prev, errorMsg]);
    
    // Show error notification
    showToast(t('aiAssistant.errorOccurred') + ': ' + errorMessage, 'error');
  }, [roomId, t, showToast]);

  // Handle streaming sources
  const handleStreamingSources = useCallback((data) => {
    const messageRoomId = data.roomId;
    
    if (!roomId || messageRoomId !== roomId) {
      console.log('🚫 Ignoring streaming_sources - room mismatch');
      return;
    }
    
    const payload = data.payload || data.data || data;
    
    console.log('📚 Processing streaming_sources message:', {
      messageId: data.messageId,
      sourceId: payload.id,
      filename: payload.original_filename
    });
    
    // Store the source by its ID for later retrieval when citations are clicked
    if (payload.id) {
      setStreamingSources(prev => {
        const updated = new Map(prev);
        updated.set(payload.id, payload);
        console.log(`✅ Stored streaming source: ${payload.id}, total sources: ${updated.size}`);
        return updated;
      });
      
      // Also add to current streaming sources array
      setCurrentStreamingSources(prev => {
        // Check if this source already exists
        const exists = prev.some(s => s.id === payload.id);
        if (exists) {
          return prev;
        }
        return [...prev, payload];
      });
    }
  }, [roomId]);

  // Handle citation clicks
  const handleCitationClick = useCallback((citationNumber, event, citationId, messageSources) => {
    event.stopPropagation();
    console.log('Citation clicked:', { citationNumber, citationId, streamingSources: streamingSources.size });
    
    // Helper function to find source by citation ID with multiple lookup strategies
    const findSourceByCitationId = (id) => {
      if (!id) return undefined;

      const trimmed = id.trim();
      if (!trimmed) return undefined;

      // Build lookup candidates
      const candidates = new Set([trimmed, trimmed.toLowerCase()]);

      // Try without prefix (e.g., "SOURCE_123" -> "123")
      const withoutPrefix = trimmed.replace(/^source[_-]?/i, '');
      if (withoutPrefix && withoutPrefix !== trimmed) {
        candidates.add(withoutPrefix);
        candidates.add(withoutPrefix.toLowerCase());
      }

      // Extract numeric value and try variations
      const numericMatch = trimmed.match(/(\d+)/);
      if (numericMatch) {
        const numericValue = numericMatch[1];
        candidates.add(numericValue);
        candidates.add(`source_${numericValue}`);
        candidates.add(`SOURCE_${numericValue}`);
        candidates.add(`citation-${numericValue}`);
      }

      // Try to find in streamingSources Map
      const candidatesArray = Array.from(candidates);
      for (let i = 0; i < candidatesArray.length; i++) {
        const candidate = candidatesArray[i];
        if (streamingSources.has(candidate)) {
          return streamingSources.get(candidate);
        }
      }

      return undefined;
    };

    // Try to find source by citation ID
    let resolvedSource = findSourceByCitationId(citationId);
    
    // Fallback: Try to find source by index in messageSources
    if (!resolvedSource && citationNumber > 0) {
      const sourceIndex = citationNumber - 1;
      if (sourceIndex >= 0 && sourceIndex < messageSources.length) {
        resolvedSource = messageSources[sourceIndex];
      }
    }

    // Fallback: Try currentStreamingSources array
    if (!resolvedSource && citationNumber > 0) {
      const sourceIndex = citationNumber - 1;
      if (sourceIndex >= 0 && sourceIndex < currentStreamingSources.length) {
        resolvedSource = currentStreamingSources[sourceIndex];
      }
    }

    // If no source found, show error
    if (!resolvedSource) {
      console.warn('No source found for citation:', { citationNumber, citationId });
      showToast(
        citationId
          ? `Quelle ${citationNumber}: Keine Quelldaten gefunden für ${citationId}.`
          : `Quelle ${citationNumber}: Keine Quelleninformationen verfügbar.`,
        'warning'
      );
      return;
    }

    console.log('Resolved source:', resolvedSource);

    // Try to open URL - check multiple URL fields
    const url = resolvedSource.embed_url
      || resolvedSource.viewer_url
      || resolvedSource.url
      || resolvedSource.link
      || resolvedSource.pdf_url;

    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      console.log('Opened source URL:', url);
      return;
    }

    // No URL available - show info alert
    const label = resolvedSource.section_title
      || resolvedSource.original_filename
      || citationId
      || `Quelle ${citationNumber}`;

    showToast(`${label}: Keine direkt öffnende URL hinterlegt.`, 'info');
  }, [streamingSources, currentStreamingSources, showToast]);

  // State for email draft preview
  const [emailDraftPreview, setEmailDraftPreview] = useState(null);

  // Handle compose email
  const handleComposeEmail = useCallback((data) => {
    console.log('📧 Compose email received:', data);
    
    const payload = data.payload || data.data || data;
    const emailData = {
      subject: payload.subject || '',
      bodyHtml: payload.body_html || '',
      bodyText: payload.body_text || '',
      toAddresses: payload.to_addresses || [],
      ccAddresses: payload.cc_addresses || [],
      bccAddresses: payload.bcc_addresses || [],
      threadId: payload.thread_id || null,
      inReplyTo: payload.in_reply_to || null,
      inReplyToDb: payload.in_reply_to_db || null
    };
    
    console.log('📧 Extracted emailData:', {
      hasBodyHtml: !!emailData.bodyHtml,
      hasBodyText: !!emailData.bodyText,
      bodyHtmlLength: emailData.bodyHtml?.length,
      bodyTextLength: emailData.bodyText?.length,
      subject: emailData.subject
    });

    // Helper to format recipients for Office.js
    const formatRecipients = (addresses) => {
      if (!addresses || !Array.isArray(addresses)) return [];
      return addresses.map(addr => {
        if (typeof addr === 'string') {
          return addr;
        }
        return addr.emailAddress || addr.address || addr;
      });
    };

    // Helper to improve HTML formatting for Outlook
    const formatHtmlForOutlook = (html) => {
      if (!html) return html;
      
      // Add margin to paragraph tags for better spacing in Outlook
      // Outlook often ignores external CSS, so we need inline styles
      let formattedHtml = html
        .replace(/<p>/g, '<p style="margin: 0 0 1em 0;">')
        .replace(/<p\s+style="([^"]*)"/g, '<p style="$1; margin: 0 0 1em 0;"');
      
      return formattedHtml;
    };

    try {
      // Check if Office.js is available
      if (typeof Office === 'undefined' || !Office.context || !Office.context.mailbox) {
        console.error('Office.js is not available');
        showToast(t('aiAssistant.officeJsNotAvailable'), 'error');
        return;
      }

      // Log Office.CoercionType for debugging
      console.log('📋 Office.CoercionType.Html:', Office.CoercionType?.Html);
      console.log('📋 Office.CoercionType.Text:', Office.CoercionType?.Text);

      const mailbox = Office.context.mailbox;
      
      // Check if we're in compose mode
      if (mailbox.item && mailbox.item.itemType === Office.MailboxEnums.ItemType.Message) {
        const isCompose = mailbox.item.subject?.setAsync !== undefined;
        
        if (isCompose) {
          // We're already in a compose window - fill the current item directly
          console.log('📝 Filling current compose window');
          
          const item = mailbox.item;
          
          // Note: If this is a reply (has threadId), we can only fill the body
          // The subject and recipients are already set by Outlook for reply forms
          const isReply = !!(emailData.threadId || emailData.inReplyTo || emailData.inReplyToDb);
          
          if (isReply) {
            console.log('📧 This is a reply - only updating body content');
            
            // For replies, only set body (subject and recipients are handled by Outlook)
            let body = emailData.bodyHtml || emailData.bodyText;
            const coercionType = emailData.bodyHtml 
              ? Office.CoercionType.Html 
              : Office.CoercionType.Text;
            
            // Format HTML for better display in Outlook
            if (emailData.bodyHtml && body) {
              body = formatHtmlForOutlook(body);
            }
            
            if (body) {
              console.log('📝 Setting reply body with coercionType:', coercionType, 'bodyHtml present:', !!emailData.bodyHtml, 'bodyText present:', !!emailData.bodyText);
              console.log('📝 Body content (first 200 chars):', body.substring(0, 200));
              
              item.body.setAsync(body, { coercionType }, (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                  console.error('Failed to set body:', result.error);
                } else {
                  console.log('✅ Reply body set successfully with coercionType:', coercionType);
                }
              });
            }
          } else {
            // For new emails, set all fields
            console.log('📧 This is a new email - setting all fields');
            
            // Set subject
            if (emailData.subject) {
              item.subject.setAsync(emailData.subject, (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                  console.error('Failed to set subject:', result.error);
                }
              });
            }
            
            // Set body (prefer HTML)
            let body = emailData.bodyHtml || emailData.bodyText;
            const coercionType = emailData.bodyHtml 
              ? Office.CoercionType.Html 
              : Office.CoercionType.Text;
            
            // Format HTML for better display in Outlook
            if (emailData.bodyHtml && body) {
              body = formatHtmlForOutlook(body);
            }
            
            if (body) {
              console.log('📝 Setting body with coercionType:', coercionType, 'bodyHtml present:', !!emailData.bodyHtml, 'bodyText present:', !!emailData.bodyText);
              console.log('📝 Body content (first 200 chars):', body.substring(0, 200));
              
              item.body.setAsync(body, { coercionType }, (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                  console.error('Failed to set body:', result.error);
                } else {
                  console.log('✅ Body set successfully with coercionType:', coercionType);
                }
              });
            }
            
            // Set recipients
            const toRecipients = formatRecipients(emailData.toAddresses);
            if (toRecipients.length > 0) {
              item.to.setAsync(toRecipients, (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                  console.error('Failed to set to recipients:', result.error);
                }
              });
            }
            
            const ccRecipients = formatRecipients(emailData.ccAddresses);
            if (ccRecipients.length > 0) {
              item.cc.setAsync(ccRecipients, (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                  console.error('Failed to set cc recipients:', result.error);
                }
              });
            }
            
            const bccRecipients = formatRecipients(emailData.bccAddresses);
            if (bccRecipients.length > 0) {
              item.bcc.setAsync(bccRecipients, (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                  console.error('Failed to set bcc recipients:', result.error);
                }
              });
            }
          }
          
          console.log('✅ Compose window filled successfully');
          
          // Add a message to the chat
          const successMessage = {
            id: generateMessageId(),
            role: 'assistant',
            content: `✅ ${t('aiAssistant.emailDraftPrepared')}`,
            timestamp: new Date()
          };
          setConversation(prev => [...prev, successMessage]);
          
          return;
        }
      }
      
      // We're NOT in compose mode - show preview instead of opening new window immediately
      console.log('📧 Showing email draft preview');
      
      // Store the draft for preview
      setEmailDraftPreview(emailData);
      
      // Add a preview message to the chat
      const toRecipients = formatRecipients(emailData.toAddresses);
      const ccRecipients = formatRecipients(emailData.ccAddresses);
      
      // Build threading context
      let threadContext = '';
      if (emailData.threadId || emailData.inReplyTo) {
        threadContext = `💬 **${t('aiAssistant.replyToConversation')}**\n`;
        if (emailData.threadId) {
          threadContext += `${t('aiAssistant.threadId')}: ${emailData.threadId}\n`;
        }
        threadContext += '\n';
      }
      
      // Add special note for replies
      let replyNote = '';
      if (emailData.threadId || emailData.inReplyTo) {
        replyNote = `\n⚠️ **Note:** To preserve email threading, please open the original email in Outlook first, then click the compose button.\n\n`;
      }
      
      const previewMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: `📧 **${t('aiAssistant.emailDraftReady')}**\n\n` +
          threadContext +
          replyNote +
          `**To:** ${toRecipients.join(', ') || '(none)'}\n` +
          (ccRecipients.length > 0 ? `**CC:** ${ccRecipients.join(', ')}\n` : '') +
          `**Subject:** ${emailData.subject || '(no subject)'}\n\n` +
          `${emailData.bodyText || emailData.bodyHtml?.replace(/<[^>]*>/g, '').substring(0, 200) || ''}...\n\n` +
          `${t('aiAssistant.openInOutlook')} 👇`,
        timestamp: new Date(),
        showComposeButton: true
      };
      setConversation(prev => [...prev, previewMessage]);
      
    } catch (error) {
      console.error('Error composing email:', error);
      showToast(t('aiAssistant.emailComposeFailed') + ': ' + error.message, 'error');
      
      const errorMsg = {
        id: generateMessageId(),
        role: 'assistant',
        content: `❌ ${t('aiAssistant.emailComposeFailed')}`,
        timestamp: new Date()
      };
      setConversation(prev => [...prev, errorMsg]);
    }
  }, [t, showToast]);

  // Helper function to create a reply using Microsoft Graph API
  const createReplyUsingGraphAPI = useCallback(async (emailData) => {
    try {
      console.log('🌐 Creating reply using Microsoft Graph API');
      console.log('🔍 Using inReplyTo message ID:', emailData.inReplyTo);
      
      if (!emailData.inReplyTo) {
        throw new Error('No inReplyTo message ID provided');
      }
      
      // Get access token for Microsoft Graph
      const tokenResult = await new Promise((resolve, reject) => {
        Office.context.mailbox.getCallbackTokenAsync({ isRest: true }, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            reject(new Error(result.error.message));
          }
        });
      });
      
      // The inReplyTo might already be in REST format or EWS format
      // Try to convert it - if it fails, it might already be REST
      let restMessageId;
      try {
        // Check if it's already a REST ID (REST IDs are shorter and URL-safe)
        if (emailData.inReplyTo.includes('==') || emailData.inReplyTo.length > 150) {
          // Looks like an EWS ID - convert it
          restMessageId = Office.context.mailbox.convertToRestId(
            emailData.inReplyTo,
            Office.MailboxEnums.RestVersion.v2_0
          );
          console.log('🔄 Converted EWS ID to REST ID:', restMessageId);
        } else {
          // Might already be REST format
          restMessageId = emailData.inReplyTo;
          console.log('✅ Using ID as-is (appears to be REST format)');
        }
      } catch (error) {
        console.warn('⚠️ ID conversion error, using as-is:', error);
        restMessageId = emailData.inReplyTo;
      }
      
      // Create reply draft using Graph API
      const restUrl = Office.context.mailbox.restUrl;
      const createReplyUrl = `${restUrl}/v2.0/me/messages/${restMessageId}/createReply`;
      
      console.log('📤 POST to:', createReplyUrl);
      
      const response = await fetch(createReplyUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenResult}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Graph API error response:', errorText);
        throw new Error(`Graph API error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const replyDraft = await response.json();
      console.log('✅ Reply draft created:', replyDraft.id);
      
      // Update the draft with our custom body
      const updateUrl = `${restUrl}/v2.0/me/messages/${replyDraft.id}`;
      const updateResponse = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${tokenResult}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          body: {
            contentType: emailData.bodyHtml ? 'HTML' : 'Text',
            content: emailData.bodyHtml || emailData.bodyText
          }
        })
      });
      
      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        console.error('❌ Update draft error response:', errorText);
        throw new Error(`Failed to update draft: ${updateResponse.status} - ${errorText}`);
      }
      
      console.log('✅ Draft body updated');
      
      // Open the draft in Outlook
      // Note: We need to convert back to EWS ID for displayExistingMessageForm
      const ewsItemId = Office.context.mailbox.convertToEwsId(
        replyDraft.id,
        Office.MailboxEnums.RestVersion.v2_0
      );
      
      console.log('🔄 Converted draft ID to EWS format:', ewsItemId);
      Office.context.mailbox.displayExistingMessageForm(ewsItemId);
      
      console.log('✅ Reply draft opened with proper threading');
      return true;
      
    } catch (error) {
      console.error('❌ Graph API reply creation failed:', error);
      console.error('Stack trace:', error.stack);
      return false;
    }
  }, []);
  
  // Function to open compose window with stored draft
  const openEmailDraft = useCallback(() => {
    if (!emailDraftPreview) return;
    
    try {
      const formatRecipients = (addresses) => {
        if (!addresses || !Array.isArray(addresses)) return [];
        return addresses.map(addr => {
          if (typeof addr === 'string') {
            return addr;
          }
          return addr.emailAddress || addr.address || addr;
        });
      };
      
      const isReply = !!(emailDraftPreview.threadId || emailDraftPreview.inReplyTo || emailDraftPreview.inReplyToDb);
      
      if (isReply) {
        // This is a REPLY - try to use displayReplyForm for proper threading
        console.log('📧 Opening reply with threading context:', {
          threadId: emailDraftPreview.threadId,
          inReplyTo: emailDraftPreview.inReplyTo
        });
        
        // Check if we're in read mode and the current item is the one we're replying to
        const mailbox = Office.context.mailbox;
        const currentItem = mailbox.item;
        
        // Helper to normalize IDs (same as backend does)
        const normalizeId = (id) => id ? id.replace(/\+/g, '_').replace(/\//g, '-') : null;
        const denormalizeId = (id) => id ? id.replace(/_/g, '+').replace(/-/g, '/') : null;
        
        // Debug logging
        const rawConversationId = currentItem?.conversationId;
        const normalizedCurrentConvId = normalizeId(rawConversationId);
        const denormalizedThreadId = denormalizeId(emailDraftPreview.threadId);
        
        console.log('🔍 Current item debug:', {
          hasItem: !!currentItem,
          itemType: currentItem?.itemType,
          currentConversationId_raw: rawConversationId,
          currentConversationId_normalized: normalizedCurrentConvId,
          expectedThreadId_raw: emailDraftPreview.threadId,
          expectedThreadId_denormalized: denormalizedThreadId,
          hasDisplayReplyAllForm: typeof currentItem?.displayReplyAllForm === 'function'
        });
        
        if (currentItem && currentItem.itemType === Office.MailboxEnums.ItemType.Message) {
          // Check if current item's conversation ID matches (comparing both raw and normalized)
          const conversationMatches = 
            rawConversationId === emailDraftPreview.threadId || 
            normalizedCurrentConvId === emailDraftPreview.threadId ||
            rawConversationId === denormalizedThreadId;
          const useCurrentItem = !emailDraftPreview.threadId || conversationMatches;
          
          console.log('🔍 Conversation match check:', {
            rawMatch: rawConversationId === emailDraftPreview.threadId,
            normalizedMatch: normalizedCurrentConvId === emailDraftPreview.threadId,
            denormalizedMatch: rawConversationId === denormalizedThreadId,
            conversationMatches,
            useCurrentItem
          });
          
          if (useCurrentItem && typeof currentItem.displayReplyAllForm === 'function') {
            // Use displayReplyAllForm to maintain threading
            const replyFormOptions = {
              htmlBody: emailDraftPreview.bodyHtml || emailDraftPreview.bodyText || ''
            };
            
            console.log('✅ Using displayReplyAllForm for proper threading');
            currentItem.displayReplyAllForm(replyFormOptions);
            
            // Note: displayReplyAllForm handles To/CC automatically, 
            // but we could also use displayReplyForm for Reply (not Reply All)
            return;
          }
        }
        
        // ALTERNATIVE: Use Microsoft Graph API to create a proper reply draft
        if (emailDraftPreview.inReplyTo) {
          console.log('🔧 Attempting to create reply using Microsoft Graph API');
          
          // Try using Graph API to create a threaded reply
          createReplyUsingGraphAPI(emailDraftPreview).then(success => {
            if (success) {
              console.log('✅ Successfully created threaded reply via Graph API');
            } else {
              console.warn('⚠️ Graph API failed, falling back to displayNewMessageForm');
              // Fall back to displayNewMessageForm
              const composeOptions = {
                subject: emailDraftPreview.subject || '',
                htmlBody: emailDraftPreview.bodyHtml || emailDraftPreview.bodyText || ''
              };
              
              const toRecipients = formatRecipients(emailDraftPreview.toAddresses);
              if (toRecipients.length > 0) {
                composeOptions.toRecipients = toRecipients;
              }
              
              Office.context.mailbox.displayNewMessageForm(composeOptions);
            }
          });
          
          // Return early - we're handling this asynchronously
          return;
        }
        
        // If we don't have inReplyTo, we can't create a threaded reply
        console.warn('⚠️ Cannot create threaded reply - no inReplyTo ID available');
        console.warn('💡 Tip: Open the original email in Outlook before asking the AI to compose a reply');
        // Continue to displayNewMessageForm below
      }
      
      // This is a NEW EMAIL or we couldn't find the original message to reply to
      const composeOptions = {
        subject: emailDraftPreview.subject || '',
        htmlBody: emailDraftPreview.bodyHtml || emailDraftPreview.bodyText || ''
      };
      
      const toRecipients = formatRecipients(emailDraftPreview.toAddresses);
      if (toRecipients.length > 0) {
        composeOptions.toRecipients = toRecipients;
      }
      
      const ccRecipients = formatRecipients(emailDraftPreview.ccAddresses);
      if (ccRecipients.length > 0) {
        composeOptions.ccRecipients = ccRecipients;
      }
      
      const bccRecipients = formatRecipients(emailDraftPreview.bccAddresses);
      if (bccRecipients.length > 0) {
        composeOptions.bccRecipients = bccRecipients;
      }
      
      Office.context.mailbox.displayNewMessageForm(composeOptions);
      
      console.log('✅ Email draft opened in new compose window');
      
      // Don't clear the preview - allow reopening multiple times
      
    } catch (error) {
      console.error('Error opening email draft:', error);
      showToast(t('aiAssistant.emailComposeFailed') + ': ' + error.message, 'error');
    }
  }, [emailDraftPreview, t, showToast]);

  // Setup message listeners
  useEffect(() => {
    if (!isConnected) return;

    console.log('🔌 Setting up message listeners for room:', roomId);

    const handleMessage = (data) => {
      console.log('📨 Message received:', data.type);
      
      switch (data.type) {
        case 'streaming_chunk':
          handleStreamingChunk(data);
          break;
        case 'streaming_complete':
        case 'streaming_end':
          handleStreamingComplete(data);
          break;
        case 'streaming_progress':
          handleProgressMessage(data);
          break;
        case 'streaming_error':
          handleStreamingError(data);
          break;
        case 'streaming_sources':
          handleStreamingSources(data);
          break;
        case 'compose_email':
          handleComposeEmail(data);
          break;
        default:
          console.log('Unknown message type:', data.type);
      }
    };

    const cleanup = setupMessageListener(handleMessage, [
      'streaming_chunk',
      'streaming_complete',
      'streaming_end',
      'streaming_progress',
      'streaming_error',
      'streaming_sources',
      'compose_email'
    ]);

    return cleanup;
  }, [
    isConnected,
    roomId,
    setupMessageListener,
    handleStreamingChunk,
    handleStreamingComplete,
    handleProgressMessage,
    handleStreamingError,
    handleStreamingSources,
    handleComposeEmail
  ]);

  // Create room on mount
  useEffect(() => {
    const initializeRoom = async () => {
      if (isConnected && !roomId) {
        try {
          console.log('🏠 Creating session room...');
          
          // Get IDs from service before creating room
          const currentThreadId = incidentCommunicationsService.getThreadId();
          const currentMessageId = incidentCommunicationsService.getMessageId();
          
          // Enhanced metadata with deduplication and tracking
          const metadata = {
            embedded: true,
            context: 'outlook-addin',
            ...(currentThreadId && { thread_id: currentThreadId }),
            ...(incidentId && { incident_id: incidentId }),
            ...(currentMessageId && { message_id: currentMessageId }),
            created_at: new Date().toISOString(),
            request_id: `outlook-ai-chat-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
          };
          
          const newRoomId = await createAndJoinSessionRoom('outlook-ai-chat', metadata);
          setRoomId(newRoomId);
          setIsRoomReady(true);
          console.log('✅ Room created:', newRoomId, 'with metadata:', metadata);
        } catch (error) {
          console.error('Failed to create room:', error);
        }
      }
    };

    initializeRoom();
  }, [isConnected, roomId, createAndJoinSessionRoom, incidentId]);

  // Fetch action prompts and context data based on message ID with smart refetching
  const fetchActionPrompts = async () => {
    setIsLoadingPrompts(true);
    try {
      const messageData = await incidentCommunicationsService.getThreadData();
      
      // Handle both success and "not found" cases gracefully
      if (messageData.success || messageData.notFound) {
        const refetchInfo = incidentCommunicationsService.getRefetchInfo();
        
        console.log('📊 Refetch Info:', refetchInfo);
        
        // Update indexed status
        const isIndexed = !messageData.notFound;
        setIsEmailIndexed(isIndexed);
        
        // Notify parent component about index status
        if (onIndexStatusChange) {
          onIndexStatusChange(isIndexed);
        }
        
        if (messageData.notFound) {
          console.warn('⚠️ Working without incident communication index - using Office.js thread ID only');
        }
        
        // Extract incident ID and thread ID (always needed)
        const extractedIncidentId = incidentCommunicationsService.extractIncidentId(messageData);
        const extractedThreadId = incidentCommunicationsService.getThreadId();
        const extractedMessageId = incidentCommunicationsService.getMessageId();
        const extractedPropertyId = incidentCommunicationsService.extractPropertyId(messageData);
        
        setIncidentId(extractedIncidentId);
        setThreadId(extractedThreadId);
        setPropertyId(extractedPropertyId);
        
        console.log('📧 Message ID:', extractedMessageId);
        console.log('🎯 Incident ID:', extractedIncidentId || 'none (not indexed yet)');
        console.log('💬 Thread ID:', extractedThreadId);
        console.log('🏢 Property ID:', extractedPropertyId || 'none');
        
        // Always update action prompts - they're message-specific (may be empty if not found)
        const prompts = incidentCommunicationsService.extractActionPrompts(messageData);
        setActionPrompts(prompts);
        console.log('✅ Action prompts updated (message-specific):', prompts);
        
        // Contact ID might change within thread, but we don't manage it here
        // It will be handled by other components (ContactInfo, etc.)
        const extractedContactId = incidentCommunicationsService.extractContactId(messageData);
        console.log('👤 Contact ID:', extractedContactId || 'none');
        
        // Return the extracted values for immediate use
        return { 
          incidentId: extractedIncidentId, 
          threadId: extractedThreadId,
          messageId: extractedMessageId,
          refetchInfo: refetchInfo
        };
      }
      return { incidentId: null, threadId: null, messageId: null, refetchInfo: null };
    } catch (error) {
      console.error('❌ Failed to fetch message data:', error);
      setActionPrompts([]);
      // Still try to use Office.js thread ID even if API fails
      const fallbackThreadId = incidentCommunicationsService.getThreadId();
      setThreadId(fallbackThreadId);
      setIncidentId(null);
      console.warn('⚠️ Using fallback mode with Office.js thread ID:', fallbackThreadId);
      return { incidentId: null, threadId: fallbackThreadId, messageId: null, refetchInfo: null };
    } finally {
      setIsLoadingPrompts(false);
    }
  };

  // Fetch action prompts when context changes (email switch)
  useEffect(() => {
    const handleEmailSwitch = async () => {
      // Fetch message data first to determine refetch strategy
      const { 
        incidentId: currentIncidentId, 
        threadId: currentThreadId, 
        messageId: currentMessageId,
        refetchInfo 
      } = await fetchActionPrompts();
      
      console.log('📧 Email/Message switched - Refetch strategy:', refetchInfo);
      
      // If it's a different thread (different incident/property), reset everything
      if (refetchInfo?.needsFullRefetch) {
        console.log('🔀 DIFFERENT THREAD - Full reset');
        
        // Leave the old room if it exists
        const oldRoomId = roomId;
        if (oldRoomId) {
          console.log('🚪 Leaving old room:', oldRoomId);
          socketLeaveRoom(oldRoomId);
        }
        
        // Reset all state for new thread
        setConversation([]);
        setMessage('');
        setIsChatExpanded(false);
        setShowActionPrompts(true);
        setStreamingResponse('');
        setIsStreaming(false);
        setChunkBuffer(new Map());
        setResponseId(null);
        setToolResultsBuffer(false);
        setPendingActionPrompt(null);
        setRoomId(null);
        setIsRoomReady(false);
        setDeepResearchEnabled(false);
        setStreamingSources(new Map());
        setCurrentStreamingSources([]);
        
        // Create and join a new room for the new thread context
        if (isConnected) {
          try {
            console.log('🏠 Creating new room for different thread...');
            
            const metadata = {
              embedded: true,
              context: 'outlook-addin',
              ...(currentThreadId && { thread_id: currentThreadId }),
              ...(currentIncidentId && { incident_id: currentIncidentId }),
              ...(currentMessageId && { message_id: currentMessageId }),
              created_at: new Date().toISOString(),
              request_id: `outlook-ai-chat-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
            };
            
            const newRoomId = await createAndJoinSessionRoom('outlook-ai-chat', metadata);
            setRoomId(newRoomId);
            setIsRoomReady(true);
            console.log('✅ New room created for new thread:', newRoomId, 'with metadata:', metadata);
          } catch (error) {
            console.error('❌ Failed to create new room for new thread:', error);
          }
        }
      } else {
        // Same thread, different message - only contact might have changed
        console.log('✅ SAME THREAD - Minimal update (contact only)');
        // Action prompts stay the same
        // Room stays the same
        // Conversation stays the same
        // Just log the message change
        console.log('📧 Message changed within same thread to:', currentMessageId);
      }
    };
    
    handleEmailSwitch();
  }, [contextVersion]);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll when conversation updates
  useEffect(() => {
    if (conversation.length > 0 || streamingResponse) {
      scrollToBottom();
    }
  }, [conversation, streamingResponse]);

  // Handle action prompt click
  const handlePromptClick = async (prompt) => {
    // Prevent duplicate processing
    if (processingActionPromptRef.current === prompt.id) {
      console.log('⏭️ Skipping duplicate action prompt:', prompt.id);
      return;
    }
    
    processingActionPromptRef.current = prompt.id;
    console.log('Action prompt clicked:', prompt);
    
    setIsChatExpanded(true);
    
    // Add user message (just display, don't send yet)
    const userMessage = {
      id: generateMessageId(),
      role: 'user',
      content: prompt.action_prompt,
      timestamp: new Date()
    };
    setConversation(prev => [...prev, userMessage]);
    
    // If there's a question, add it as assistant message with streaming effect
    if (prompt.question_for_details) {
      setTimeout(() => {
        const fullQuestion = prompt.question_for_details;
        const questionTimestamp = new Date();
        
        // Create a unique ID for this streaming message
        const streamingMessageId = `streaming-${Date.now()}`;
        
        // Add initial empty assistant message
        const assistantQuestion = {
          id: streamingMessageId,
          role: 'assistant',
          content: '',
          timestamp: questionTimestamp,
          isStreaming: true,
          streamingId: streamingMessageId
        };
        setConversation(prev => [...prev, assistantQuestion]);
        
        // Stream the question character by character
        let currentIndex = 0;
        const chunkSize = 4; // Characters per chunk (adjust for speed)
        const intervalDelay = 15; // ms between chunks (adjust for speed)
        
        const streamInterval = setInterval(() => {
          if (currentIndex < fullQuestion.length) {
            const nextChunk = fullQuestion.slice(0, currentIndex + chunkSize);
            currentIndex += chunkSize;
            
            // Update the streaming message
            setConversation(prev => prev.map(msg => 
              msg.streamingId === streamingMessageId
                ? { ...msg, content: nextChunk }
                : msg
            ));
          } else {
            // Streaming complete
            clearInterval(streamInterval);
            
            // Finalize the message (remove streaming flag)
            setConversation(prev => prev.map(msg => 
              msg.streamingId === streamingMessageId
                ? { ...msg, content: fullQuestion, isStreaming: false }
                : msg
            ));
            
            // Store the action prompt for the next message
            setPendingActionPrompt(prompt);
            
            // Focus textarea
            textareaRef.current?.focus();
            
            // Reset processing ref
            processingActionPromptRef.current = null;
          }
        }, intervalDelay);
      }, 500); // Initial delay of 500ms (0.5 seconds)
    } else {
      // No follow-up question, just reset
      processingActionPromptRef.current = null;
    }
    
    setShowActionPrompts(false);
  };

  // Handle research button click (toggle deep research)
  const handleResearchClick = () => {
    setDeepResearchEnabled(prev => !prev);
    console.log('Deep research toggled:', !deepResearchEnabled);
  };

  // Handle summary button click (auto-send summary request)
  const handleSummaryClick = async () => {
    if (isSending) {
      console.log('Cannot send summary message:', { isSending, roomId, isRoomReady });
      return;
    }
    if (!roomId || !isRoomReady) {
      const ensured = await ensureRoomReady();
      if (!ensured) return;
    }

    const summaryMessage = 'Fasse mir die bisherige Kommuniaktion zusammen';
    
    setIsChatExpanded(true);
    setIsSending(true);
    setShowActionPrompts(false);

    // Reset streaming state
    setStreamingResponse('');
    setChunkBuffer(new Map());
    setIsStreamingComplete(false);
    setExpectedFinalSequence(null);
    setCurrentProgress(t('aiAssistant.analyzingMessage'));

    // Add user message to conversation
    const userMessage = {
      id: generateMessageId(),
      role: 'user',
      content: summaryMessage,
      timestamp: new Date()
    };
    setConversation(prev => [...prev, userMessage]);

    try {
      // Get current message ID
      const currentMessageId = incidentCommunicationsService.getMessageId();
      
      // Build payload
      const chatPayload = {
        type: 'chat_message',
        roomId: roomId,
        payload: {
          message: summaryMessage,
          response_id: responseId,
          tool_results_buffer: toolResultsBuffer,
          outlook_addin: true,
          settings: {
            web_search: false,
            deep_research: deepResearchEnabled
          },
          ...(threadId && { thread_id: threadId }),
          ...(propertyId && { property_id: propertyId }),
          ...(currentMessageId && { message_id: currentMessageId })
        },
        timestamp: new Date().toISOString(),
        data: {
          source: 'outlook_ai_chat',
          room_id: roomId
        }
      };

      console.log('📤 Sending summary request with thread_id:', threadId, 'incident_id:', incidentId, 'property_id:', propertyId);
      console.log('📤 Full payload:', chatPayload);

      const response = await streamingApiClient.processMessage(chatPayload);

      if (response && response.success !== false) {
        console.log('✅ Summary request sent successfully');
        
        // Update response ID if provided
        if (response.response_id) {
          setResponseId(response.response_id);
        }
      } else {
        throw new Error(response?.message || 'Failed to send summary request');
      }
    } catch (error) {
      console.error('Error sending summary request:', error);
      
      const errorMsg = {
        id: generateMessageId(),
        role: 'assistant',
        content: t('aiAssistant.errorOccurred'),
        timestamp: new Date()
      };
      setConversation(prev => [...prev, errorMsg]);
      
      showToast(t('aiAssistant.errorOccurred') + ': ' + error.message, 'error');
    } finally {
      setIsSending(false);
    }
  };

  // Send message to AI assistant
  const handleSendMessage = async () => {
    if (!message.trim() || isSending) {
      console.log('Cannot send message:', { 
        hasMessage: message.trim(), 
        isSending, 
        roomId, 
        isRoomReady 
      });
      return;
    }
    if (!roomId || !isRoomReady) {
      const ensured = await ensureRoomReady();
      if (!ensured) return;
    }

    const userMessageContent = message;
    const actionPromptToSend = pendingActionPrompt;
    
    setMessage('');
    setIsSending(true);
    setIsChatExpanded(true);
    setShowActionPrompts(false);

    // Reset streaming state
    setStreamingResponse('');
    setChunkBuffer(new Map());
    setIsStreamingComplete(false);
    setExpectedFinalSequence(null);
    setCurrentProgress(t('aiAssistant.analyzingMessage'));

    // Add user message to conversation
    const userMessage = {
      id: generateMessageId(),
      role: 'user',
      content: userMessageContent,
      timestamp: new Date()
    };
    setConversation(prev => [...prev, userMessage]);

    try {
      // Get current message ID
      const currentMessageId = incidentCommunicationsService.getMessageId();
      
      // Build payload
      const chatPayload = {
        type: 'chat_message',
        roomId: roomId,
        ...(incidentId && { incident_id: incidentId }),
        payload: {
          message: userMessageContent,
          response_id: responseId,
          tool_results_buffer: toolResultsBuffer,
          outlook_addin: true,
          settings: {
            web_search: false,
            deep_research: deepResearchEnabled
          },
          ...(threadId && { thread_id: threadId }),
          ...(propertyId && { property_id: propertyId }),
          ...(currentMessageId && { message_id: currentMessageId }),
          ...(actionPromptToSend && {
            action_prompt: {
              id: actionPromptToSend.id,
              display_action: actionPromptToSend.display_action,
              action_prompt: actionPromptToSend.action_prompt,
              question_for_details: actionPromptToSend.question_for_details
            }
          })
        },
        timestamp: new Date().toISOString(),
        data: {
          source: 'outlook_ai_chat',
          room_id: roomId
        }
      };

      // Clear pending action prompt
      if (actionPromptToSend) {
        setPendingActionPrompt(null);
        console.log('✅ Action prompt sent with message');
      }

      console.log('📤 Sending message with thread_id:', threadId, 'message_id:', currentMessageId, 'incident_id:', incidentId, 'property_id:', propertyId);
      console.log('📤 Full payload:', chatPayload);

      const response = await streamingApiClient.processMessage(chatPayload);

      if (response && response.success !== false) {
        console.log('✅ Message sent successfully');
        
        // Update response ID if provided
        if (response.response_id) {
          setResponseId(response.response_id);
        }
      } else {
        throw new Error(response?.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMsg = {
        id: generateMessageId(),
        role: 'assistant',
        content: t('aiAssistant.errorOccurred'),
        timestamp: new Date()
      };
      setConversation(prev => [...prev, errorMsg]);
      
      showToast(t('aiAssistant.errorOccurred') + ': ' + error.message, 'error');
    } finally {
      setIsSending(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Ensure a room exists and is joined; create on-demand when needed
  const ensureRoomReady = useCallback(async () => {
    if (roomId && isRoomReady) return roomId;
    if (!isConnected) return null;
    if (isCreatingRoom) return null;

    try {
      setIsCreatingRoom(true);
      const currentThreadId = incidentCommunicationsService.getThreadId();
      const currentMessageId = incidentCommunicationsService.getMessageId();
      const metadata = {
        embedded: true,
        context: 'outlook-addin',
        ...(currentThreadId && { thread_id: currentThreadId }),
        ...(incidentId && { incident_id: incidentId }),
        ...(currentMessageId && { message_id: currentMessageId }),
        created_at: new Date().toISOString(),
        request_id: `outlook-ai-chat-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
      };
      const newRoomId = await createAndJoinSessionRoom('outlook-ai-chat', metadata);
      setRoomId(newRoomId);
      setIsRoomReady(true);
      return newRoomId;
    } catch (error) {
      console.error('Failed to ensure room ready:', error);
      return null;
    } finally {
      setIsCreatingRoom(false);
    }
  }, [roomId, isRoomReady, isConnected, incidentId, isCreatingRoom, createAndJoinSessionRoom]);

  // Handle textarea change
  const handleTextareaChange = (e) => {
    setMessage(e.target.value);
    const hasText = e.target.value.trim().length > 0;
    if (hasText && !roomId && !isRoomReady) {
      // Create room in background when user starts typing
      ensureRoomReady();
    }
  };

  // Reset chat - leave old room and join new one
  const handleResetChat = async () => {
    // Leave the old room if it exists
    const oldRoomId = roomId;
    if (oldRoomId) {
      console.log('🚪 Leaving old room:', oldRoomId);
      socketLeaveRoom(oldRoomId);
    }
    
    // Reset all state
    setConversation([]);
    setMessage('');
    setShowActionPrompts(true);
    setIsStreaming(false);
    setIsSending(false);
    setStreamingResponse('');
    setChunkBuffer(new Map());
    setResponseId(null);
    setToolResultsBuffer(false);
    setPendingActionPrompt(null);
    setIncidentId(null);
    setThreadId(null);
    setRoomId(null);
    setIsRoomReady(false);
    setDeepResearchEnabled(false);
    setStreamingSources(new Map());
    setCurrentStreamingSources([]);
    
    // Fetch message data to get current context
    const { 
      incidentId: currentIncidentId, 
      threadId: currentThreadId, 
      messageId: currentMessageId 
    } = await fetchActionPrompts();
    
    // Create and join a new room
    if (isConnected) {
      try {
        console.log('🏠 Creating new room after reset...');
        
        // Enhanced metadata with deduplication and tracking
        const metadata = {
          embedded: true,
          context: 'outlook-addin',
          ...(currentThreadId && { thread_id: currentThreadId }),
          ...(currentIncidentId && { incident_id: currentIncidentId }),
          ...(currentMessageId && { message_id: currentMessageId }),
          created_at: new Date().toISOString(),
          request_id: `outlook-ai-chat-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
        };
        
        const newRoomId = await createAndJoinSessionRoom('outlook-ai-chat', metadata);
        setRoomId(newRoomId);
        setIsRoomReady(true);
        console.log('✅ New room created after reset:', newRoomId, 'with metadata:', metadata);
      } catch (error) {
        console.error('Failed to create new room after reset:', error);
      }
    }
  };

  // Handle saving unindexed email to backend
  const handleSaveEmailToBackend = async () => {
    if (isSavingEmail) {
      console.log('⏭️ Already saving email, skipping duplicate request');
      return;
    }

    setIsSavingEmail(true);
    
    try {
      console.log('💾 Starting to save unindexed email to backend...');

      // Step 1: Get current email content from Office.js
      const emailContent = await emailMessagesService.getCurrentEmailContent();
      if (!emailContent || !emailContent.id) {
        throw new Error('Could not get email content from current item');
      }
      console.log('✅ Email content retrieved:', emailContent.id);

      // Step 2: Determine the inbox email address
      // For received emails, the inbox email is in the recipients
      // For sent emails, it's the sender
      let inboxEmail = null;
      
      const userEmail = Office?.context?.mailbox?.userProfile?.emailAddress;
      
      // Check To recipients
      const toEmails = emailContent.toRecipients?.map(r => r.emailAddress?.address?.toLowerCase()) || [];
      // Check CC recipients
      const ccEmails = emailContent.ccRecipients?.map(r => r.emailAddress?.address?.toLowerCase()) || [];
      // Check BCC recipients (if available)
      const bccEmails = emailContent.bccRecipients?.map(r => r.emailAddress?.address?.toLowerCase()) || [];
      
      // Combine all recipient emails
      const allRecipientEmails = [...toEmails, ...ccEmails, ...bccEmails].filter(Boolean);
      
      // Get user's connections to check which inbox this email belongs to
      const connections = await emailConnectionsService.getAll();
      const connectionEmails = connections.map(c => c.email_address?.toLowerCase());
      
      // Find which of our connected inboxes received this email
      const matchingInboxEmail = allRecipientEmails.find(email => 
        connectionEmails.includes(email)
      );
      
      if (matchingInboxEmail) {
        // This is a received email - use the matching inbox email
        inboxEmail = matchingInboxEmail;
        console.log('📬 Received email - inbox:', inboxEmail);
      } else {
        // This might be a sent email - use the sender email
        const senderEmail = emailContent.from?.emailAddress?.address || 
                           emailContent.sender?.emailAddress?.address ||
                           userEmail;
        inboxEmail = senderEmail?.toLowerCase();
        console.log('📤 Sent email - sender:', inboxEmail);
      }

      if (!inboxEmail) {
        throw new Error('Could not determine inbox email address');
      }

      // Step 3: Prepare connection object with just email_address
      // Backend will handle finding the connection by email_address
      const connectionPayload = {
        email_address: inboxEmail
      };

      console.log('📤 Sending email to backend with inbox:', inboxEmail);

      // Step 4: Send to backend (backend will match connection by email_address)
      const result = await emailMessagesService.createOutlookEmailMessage(
        connectionPayload,
        emailContent
      );

      if (result.success) {
        console.log('✅ Email successfully saved to backend');
        
        // Show success message
        showToast(t('aiAssistant.emailIndexedSuccessfully') || 'Email successfully indexed!', 'success');
        
        // Refresh the message data to update indexed status
        await fetchActionPrompts();
      }
    } catch (error) {
      console.error('❌ Failed to save email to backend:', error);
      showToast(
        t('aiAssistant.emailIndexingFailed') || 
        'Failed to index email. Please try again or contact support.',
        'error'
      );
    } finally {
      setIsSavingEmail(false);
    }
  };

  // Compact view (when not in chat)
  if (!isChatExpanded) {
    return (
      <>
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        <div className="border-b border-gray-300 bg-white p-4 space-y-4">
        {/* Kira/Ben Header with Icon */}
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
            <img 
              src={deepResearchEnabled ? "../../assets/Ben.png" : "../../assets/Kira.png"}
              alt={deepResearchEnabled ? t('aiAssistant.benName') : t('aiAssistant.kiraName')}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex-1">
            <h2 className={`${currentTextSize.headerText} font-semibold text-gray-900`}>
              {deepResearchEnabled ? t('aiAssistant.benName') : t('aiAssistant.kiraName')}
            </h2>
            <p className={`${currentTextSize.buttonText} text-gray-600`}>
              {deepResearchEnabled ? t('aiAssistant.benSubtitle') : t('aiAssistant.kiraSubtitle')}
            </p>
          </div>
        </div>

        {/* Not Indexed Warning */}
        {!isEmailIndexed && (
          <div className={`${currentTextSize.buttonText} text-blue-700 bg-blue-50 border border-blue-200 p-3 rounded-lg`}>
            <div className="flex items-start gap-2">
              <svg 
                className="h-5 w-5 flex-shrink-0 mt-0.5" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
                  clipRule="evenodd" 
                />
              </svg>
              <div className="flex-1">
                <div className="font-medium">{t('aiAssistant.emailNotIndexed')}</div>
                <div className={`${currentTextSize.buttonText} text-blue-600 mt-0.5`}>
                  {t('aiAssistant.emailNotIndexedDescription')}
                </div>
              </div>
            </div>
            <button
              onClick={handleSaveEmailToBackend}
              disabled={isSavingEmail}
              className="mt-2 w-full px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {isSavingEmail ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{t('aiAssistant.indexingEmail') || 'Indexing...'}</span>
                </>
              ) : (
                <span>{t('aiAssistant.indexEmailNow') || 'Index Email Now'}</span>
              )}
            </button>
          </div>
        )}

        {/* Connection status */}
        {!isConnected && (
          <div className={`${currentTextSize.buttonText} text-amber-600 bg-amber-50 p-2 rounded`}>
            {t('aiAssistant.connecting')}...
          </div>
        )}

        {/* Action Prompt Bubbles - hide when research mode is enabled */}
        {showActionPrompts && !deepResearchEnabled && (
          isLoadingPrompts ? (
            <div className={`flex items-center gap-2 ${currentTextSize.messageText} text-gray-500 mb-3`}>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
              <span>{t('aiAssistant.loadingSuggestions')}</span>
            </div>
          ) : (
            <ActionPromptBubbles 
              actionPrompts={actionPrompts}
              onPromptClick={handlePromptClick}
            />
          )
        )}

        {/* Message Input */}
        <div className="space-y-2">
          <textarea 
            ref={textareaRef}
            placeholder={t('aiAssistant.messagePlaceholder')}
            className={`w-full ${currentTextSize.minHeight} ${currentTextSize.padding} ${currentTextSize.inputText} border border-gray-300 rounded-md resize-y focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
            value={message}
            onChange={handleTextareaChange}
            onKeyPress={handleKeyPress}
            disabled={isSending || !isRoomReady}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            <button 
              onClick={handleResearchClick}
              className={`h-8 w-8 p-0 rounded flex items-center justify-center transition-colors ${
                deepResearchEnabled 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'text-blue-600 hover:bg-blue-50'
              }`} 
              title={t('aiAssistant.research')}
            >
              <BookOpenRegular className="h-4 w-4" />
            </button>
            <button 
              onClick={handleSummaryClick}
              disabled={isSending}
              className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50 rounded flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" 
              title={t('aiAssistant.createSummary')}
            >
              <SparkleRegular className="h-4 w-4" />
            </button>
          </div>
          
          {/* Send Button */}
          <button
            onClick={handleSendMessage}
            disabled={!message.trim() || isSending || !isRoomReady}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            title={!isRoomReady ? t('aiAssistant.connectingToRoom') : ''}
          >
            {isSending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <SendRegular className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
      </>
    );
  }

  // Expanded chat view
  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
    <div className="flex flex-col h-full bg-white border-b border-gray-300">
      {/* Sticky Top Bar */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-2">
          <div className={`${currentTextSize.avatarSize} rounded-full overflow-hidden bg-gray-100 flex items-center justify-center`}>
            <img 
              src={deepResearchEnabled ? "../../assets/Ben.png" : "../../assets/Kira.png"}
              alt={deepResearchEnabled ? t('aiAssistant.benName') : t('aiAssistant.kiraName')}
              className="h-full w-full object-cover"
            />
          </div>
          <span className={`${currentTextSize.headerText} font-medium text-gray-900`}>
            {deepResearchEnabled ? t('aiAssistant.chatWithBen') : t('aiAssistant.chatWithKira')}
          </span>
          {!isConnected && (
            <span className={`${currentTextSize.buttonText} text-amber-600`}>({t('aiAssistant.connecting')}...)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Reset Chat Button */}
          <button 
            onClick={handleResetChat}
            className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100"
            title={t('aiAssistant.resetChat')}
          >
            <svg 
              className="h-5 w-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Not Indexed Warning */}
        {!isEmailIndexed && (
          <div className={`${currentTextSize.buttonText} text-blue-700 bg-blue-50 border border-blue-200 p-3 rounded-lg`}>
            <div className="flex items-start gap-2">
              <svg 
                className="h-5 w-5 flex-shrink-0 mt-0.5" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
                  clipRule="evenodd" 
                />
              </svg>
              <div className="flex-1">
                <div className="font-medium">{t('aiAssistant.emailNotIndexed')}</div>
                <div className={`${currentTextSize.buttonText} text-blue-600 mt-0.5`}>
                  {t('aiAssistant.emailNotIndexedDescription')}
                </div>
              </div>
            </div>
            <button
              onClick={handleSaveEmailToBackend}
              disabled={isSavingEmail}
              className="mt-2 w-full px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {isSavingEmail ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{t('aiAssistant.indexingEmail') || 'Indexing...'}</span>
                </>
              ) : (
                <span>{t('aiAssistant.indexEmailNow') || 'Index Email Now'}</span>
              )}
            </button>
          </div>
        )}
        
        {/* Action Prompt Bubbles at top - hide when research mode is enabled */}
        {showActionPrompts && conversation.length === 0 && !deepResearchEnabled && (
          <div className="mb-4">
            {isLoadingPrompts ? (
              <div className={`flex items-center gap-2 ${currentTextSize.messageText} text-gray-500`}>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                <span>{t('aiAssistant.loadingSuggestions')}</span>
              </div>
            ) : (
              <ActionPromptBubbles 
                actionPrompts={actionPrompts}
                onPromptClick={handlePromptClick}
              />
            )}
          </div>
        )}
        
        {conversation.map((msg, index) => (
          <ChatMessage 
            key={
              msg.id ||
              msg.streamingId ||
              `${msg.role}-${typeof msg.timestamp === 'string' ? Date.parse(msg.timestamp) : (msg.timestamp?.getTime?.() || index)}`
            }
            message={msg}
            textSizeClasses={currentTextSize}
            onComposeClick={msg.showComposeButton ? openEmailDraft : undefined}
            onCitationClick={handleCitationClick}
            streamingSources={streamingSources}
            deepResearchEnabled={deepResearchEnabled}
          />
        ))}
        
        {/* Progress Indicator */}
        {currentProgress && !isStreaming && (
          <div className="flex justify-start">
            <div className={`${currentTextSize.messageText} text-gray-500 italic`}>
              {currentProgress}
              <span className="ml-1 animate-pulse">...</span>
            </div>
          </div>
        )}
        
        {/* Streaming Response */}
        {isStreaming && streamingResponse && (
          <ChatMessage 
            message={{
              role: 'assistant',
              content: streamingResponse,
              timestamp: new Date(),
              sources: currentStreamingSources
            }}
            isStreaming={true}
            textSizeClasses={currentTextSize}
            onComposeClick={undefined}
            onCitationClick={handleCitationClick}
            streamingSources={streamingSources}
            deepResearchEnabled={deepResearchEnabled}
          />
        )}
        
        {/* Typing Indicator */}
        {isStreaming && !streamingResponse && (
          <div className="flex justify-start mb-4">
            <div className="flex gap-3 max-w-[80%]">
              <div className={`flex-shrink-0 ${currentTextSize.avatarSize} rounded-full bg-gray-100 flex items-center justify-center`}>
                <img 
                  src={deepResearchEnabled ? "../../assets/Ben.png" : "../../assets/Kira.png"}
                  alt={deepResearchEnabled ? t('aiAssistant.benName') : t('aiAssistant.kiraName')}
                  className="h-full w-full rounded-full object-cover"
                />
              </div>
              <div className={`bg-gray-100 rounded-lg ${currentTextSize.padding}`}>
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 p-4 bg-white">
        <div className="flex items-end gap-2">
          <textarea 
            ref={textareaRef}
            placeholder={t('aiAssistant.messagePlaceholder')}
            className={`flex-1 ${currentTextSize.minHeight} max-h-[120px] ${currentTextSize.padding} ${currentTextSize.inputText} border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
            value={message}
            onChange={handleTextareaChange}
            onKeyPress={handleKeyPress}
            disabled={isSending}
            rows={2}
          />
          <button
            onClick={handleSendMessage}
            disabled={!message.trim() || isSending}
            className={`${currentTextSize.minHeight} px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2`}
          >
            {isSending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <SendRegular className="h-5 w-5" />
            )}
          </button>
        </div>
        
        {/* Action Buttons in Chat View */}
        <div className="mt-2 flex gap-2">
          <button 
            onClick={handleResearchClick}
            className={`h-7 w-7 p-0 rounded flex items-center justify-center transition-colors ${
              deepResearchEnabled 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'text-blue-600 hover:bg-blue-50'
            }`} 
            title={t('aiAssistant.research')}
          >
            <BookOpenRegular className="h-4 w-4" />
          </button>
          <button 
            onClick={handleSummaryClick}
            disabled={isSending}
            className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50 rounded flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" 
            title={t('aiAssistant.createSummary')}
          >
            <SparkleRegular className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
